/**
 * Users Domain API Types
 * Zod schemas for Strapi user model and API responses
 */

import { z } from '@ufc/core'

// ===========================================
// Strapi Schemas (matching backend model)
// ===========================================

// Strapi Role Schema
export const strapiRoleDtoSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.enum([
    'super_admin',
    'admin',
    'operator',
    'kiosk_user',
    'tenant_admin',
    'tenant_user',
    'sales_office_user',
    'finance_admin',
    'security_guard',
    'employee',
    'customer',
    'customer_operator',
    'authenticated',
    'public',
    'ecommerce',
  ]),
})

export type StrapiRoleDto = z.infer<typeof strapiRoleDtoSchema>

// Tenant Reference Schema (when embedded in user)
export const tenantRefDtoSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  name: z.string(),
  code: z.string(),
  tenantStatus: z.enum(['pending', 'active', 'inactive', 'suspended']).optional(),
  channel: z.enum(['b2b', 'ecommerce']).optional(),
})

export type TenantRefDto = z.infer<typeof tenantRefDtoSchema>

// User DTO Schema (Strapi users-permissions plugin model)
export const userDtoSchema = z.object({
  id: z.number(),
  documentId: z.string().optional(),
  username: z.string().optional(),
  email: z.string().email(),
  fullName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  provider: z.string().optional(),
  confirmed: z.boolean().optional(),
  blocked: z.boolean().optional(),
  attemptBlocked: z.boolean().optional(),
  userStatus: z.enum(['active', 'inactive', 'invited']).optional(),
  role: strapiRoleDtoSchema.nullable().optional(),
  tenant: tenantRefDtoSchema.nullable().optional(),
  factory: z
    .object({
      id: z.number(),
      documentId: z.string().optional(),
      name: z.string().optional(),
      code: z.string().optional(),
    })
    .nullable()
    .optional(),
  location: z
    .object({
      id: z.number(),
      documentId: z.string().optional(),
      name: z.string().optional(),
      code: z.string().optional(),
    })
    .nullable()
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type UserDto = z.infer<typeof userDtoSchema>

// User Role & Status Enums (for UI)
export const userRoleSchema = z.enum(['admin', 'manager', 'operator', 'viewer'])
export type UserRoleDto = z.infer<typeof userRoleSchema>

export const userStatusSchema = z.enum(['active', 'inactive', 'invited', 'blocked'])
export type UserStatusDto = z.infer<typeof userStatusSchema>

// ===========================================
// Strapi List Response Schema
// ===========================================

export const userStatsSchema = z.object({
  total: z.number(),
  active: z.number(),
  inactive: z.number(),
  blocked: z.number(),
  invited: z.number(),
})

export type UserStatsDto = z.infer<typeof userStatsSchema>

export const strapiUserListResponseSchema = z.object({
  data: z.array(userDtoSchema),
  meta: z.object({
    pagination: z.object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    }),
    stats: userStatsSchema.optional(),
  }).optional(),
})

export type StrapiUserListResponse = z.infer<typeof strapiUserListResponseSchema>

// ===========================================
// Request Schemas (for creating/updating users)
// ===========================================

// Create user request (Strapi format)
export const createUserRequestSchema = z.object({
  username: z.string().optional(),
  email: z.string().email('Valid email is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  fullName: z.string().min(1, 'Name is required'),
  phone: z.string().optional(),
  companyName: z.string().optional(),
  role: z.number(), // Role ID
  tenant: z.number().optional(), // Tenant ID
  factory: z.number().optional(), // Factory ID
  location: z.number().optional(), // Location ID
  confirmed: z.boolean().optional(),
  blocked: z.boolean().optional(),
})

export type CreateUserRequestDto = z.infer<typeof createUserRequestSchema>

// Update user request
export const updateUserRequestSchema = z.object({
  fullName: z.string().min(1).optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  companyName: z.string().optional(),
  role: z.number().optional(),
  factory: z.number().nullable().optional(),
  location: z.number().nullable().optional(),
  confirmed: z.boolean().optional(),
  blocked: z.boolean().optional(),
  userStatus: z.enum(['active', 'inactive', 'invited']).optional(),
})

export type UpdateUserRequestDto = z.infer<typeof updateUserRequestSchema>

// ===========================================
// Query Schemas
// ===========================================

export const userListQuerySchema = z.object({
  'filters[blocked][$eq]': z.boolean().optional(),
  'filters[confirmed][$eq]': z.boolean().optional(),
  'filters[role][type][$eq]': z.string().optional(),
  'filters[tenant][id][$eq]': z.number().optional(),
  'filters[$or][0][fullName][$containsi]': z.string().optional(),
  'filters[$or][1][email][$containsi]': z.string().optional(),
  'populate[role]': z.literal(true).optional(),
  'populate[tenant]': z.literal(true).optional(),
  'pagination[page]': z.number().optional(),
  'pagination[pageSize]': z.number().optional(),
  'sort': z.string().optional(),
})

export type UserListQueryDto = z.infer<typeof userListQuerySchema>

// ===========================================
// Validation Helpers
// ===========================================

export function validateUserDto(data: unknown): UserDto {
  return userDtoSchema.parse(data)
}

export function validateUserListResponse(data: unknown): StrapiUserListResponse {
  return strapiUserListResponseSchema.parse(data)
}

export function validateCreateUserRequest(data: unknown): CreateUserRequestDto {
  return createUserRequestSchema.parse(data)
}

export function validateUpdateUserRequest(data: unknown): UpdateUserRequestDto {
  return updateUserRequestSchema.parse(data)
}

// ===========================================
// API Endpoints (Strapi users-permissions)
// ===========================================

export const USER_ENDPOINTS = {
  // Admin endpoint with proper pagination support
  LIST: '/api/admin/users',
  // Standard users-permissions endpoints
  DETAIL: (id: string) => `/api/users/${id}`,
  CREATE: '/api/admin/users',
  UPDATE: (id: string) => `/api/admin/users/${id}`,
  DELETE: (id: string) => `/api/users/${id}`,
  CHANGE_ROLE: (id: string) => `/api/admin/users/${id}`,
  RESET_PASSWORD: (id: string) => `/api/admin/users/${id}/reset-password`,
  UNLOCK_ATTEMPTS: (id: string) => `/api/admin/users/${id}/unlock-attempts`,
} as const
